import { styled, Typography } from '@mui/material';
import { DateRangePicker } from 'components/DateRangePicker/DateRangePicker';
import TimePicker from 'components/TimePicker';
import { Label } from 'components/Widget/Form/Form';

const DateTimeInput = styled('div', {
    shouldForwardProp: prop => prop !== '$error' && prop !== '$disabled',
})<{ $error: boolean; $disabled: boolean }>(({ theme, $error, $disabled }) => ({
    'display': 'grid',
    'gridTemplateColumns': 'minmax(0, 3fr) minmax(0, 2fr)',
    'alignItems': 'center',
    'border': `1px solid ${$error ? theme.palette.error.main : theme.palette.grey[400]}`,
    'borderRadius': theme.shape.borderRadius,
    'backgroundColor': $disabled
        ? theme.palette.grey[50]
        : theme.palette.background.paper,
    '&:focus-within': {
        outline: `2px solid ${$error ? theme.palette.error.main : theme.palette.primary.main}`,
        outlineOffset: -1,
    },
}));

const TimeInput = styled('div')(({ theme }) => ({
    'position': 'relative',
    'minWidth': 0,
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        borderLeft: `1px solid ${theme.palette.grey[300]}`,
    },
}));

const joinedInputStyle = {
    borderColor: 'transparent',
    borderRadius: 0,
    backgroundColor: 'transparent',
};

interface Props {
    label: string;
    date: string;
    time: string;
    minDate: string;
    maxDate: string;
    onDateChange: (date: string) => void;
    onTimeChange: (time: string) => void;
    autoFocus?: boolean;
    error?: string;
    disabled: boolean;
}

export function DateTimeField({
    label,
    date,
    time,
    minDate,
    maxDate,
    onDateChange,
    onTimeChange,
    autoFocus,
    error,
    disabled,
}: Props) {
    return (
        <div>
            <Label text={label} />
            <DateTimeInput $error={!!error} $disabled={disabled}>
                <DateRangePicker
                    variant='day'
                    tabIndex={-1}
                    period={{ start: date, end: date }}
                    onChange={period => {
                        if (period) {
                            onDateChange(period.start);
                        }
                    }}
                    disableBefore={minDate}
                    disableAfter={maxDate}
                    startAdornment='calendar_today'
                    startAdornmentSx={{ pr: 1 }}
                    endAdornment={null}
                    variantSx={joinedInputStyle}
                    disabled={disabled}
                    fullWidth
                />
                <TimeInput>
                    <TimePicker
                        value={time}
                        onChange={onTimeChange}
                        live
                        autoFocus={autoFocus}
                        adornmentPosition='start'
                        toggleTabIndex={-1}
                        startAdornmentSx={{ pr: 1 }}
                        variantSx={joinedInputStyle}
                        disabled={disabled}
                        fullWidth
                    />
                </TimeInput>
            </DateTimeInput>
            {error && (
                <Typography variant='caption' color='error'>
                    {error}
                </Typography>
            )}
        </div>
    );
}
